import { createId } from "@paralleldrive/cuid2";
import {
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { z } from "zod";
import { createdAt, deletedAt, updatedAt } from "../lib/types";
import { cuid, dateTime, string } from "../validator/common";

export const CatalogItemType = {
  DIGITAL_PROGRAM: "digital_program",
  DIGITAL_BUNDLE: "digital_bundle",
  COACHING: "coaching",
  CONSULTING: "consulting",
  PHYSICAL_PRODUCT: "physical_product",
} as const;

export const CatalogItemStatus = {
  DRAFT: "draft",
  ACTIVE: "active",
  ARCHIVED: "archived",
} as const;

export const BillingInterval = {
  ONE_TIME: "one_time",
  MONTH: "month",
} as const;

export const FulfillmentType = {
  PROGRAM_ACCESS: "program_access",
  YOUTUBE_EMAIL: "youtube_email",
  COACHING_ACCESS: "coaching_access",
  CONSULTING_SERVICE: "consulting_service",
} as const;

export const PromotionStatus = {
  DRAFT: "draft",
  ACTIVE: "active",
  ENDED: "ended",
} as const;

export const catalogItems = sqliteTable(
  "catalog_items",
  {
    catalogItemId: text("catalog_item_id")
      .primaryKey()
      .notNull()
      .$defaultFn(createId),
    slug: text("slug").notNull(),
    itemType: text("item_type")
      .notNull()
      .$type<(typeof CatalogItemType)[keyof typeof CatalogItemType]>(),
    name: text("name").notNull(),
    description: text("description").notNull(),
    imageUrl: text("image_url"),
    status: text("status")
      .notNull()
      .default(CatalogItemStatus.DRAFT)
      .$type<(typeof CatalogItemStatus)[keyof typeof CatalogItemStatus]>(),
    isFeatured: integer("is_featured", { mode: "boolean" })
      .notNull()
      .default(false),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt,
    updatedAt,
    deletedAt,
  },
  (t) => [
    uniqueIndex("catalog_items_slug_unique").on(t.slug),
    index("catalog_items_status_order_idx").on(t.status, t.displayOrder),
  ],
);

export const catalogPrices = sqliteTable(
  "catalog_prices",
  {
    catalogPriceId: text("catalog_price_id")
      .primaryKey()
      .notNull()
      .$defaultFn(createId),
    catalogItemId: text("catalog_item_id")
      .notNull()
      .references(() => catalogItems.catalogItemId, { onDelete: "cascade" }),
    amountKurus: integer("amount_kurus").notNull(),
    currency: text("currency").notNull().default("TRY"),
    vatRateBasisPoints: integer("vat_rate_basis_points")
      .notNull()
      .default(2000),
    vatIncluded: integer("vat_included", { mode: "boolean" })
      .notNull()
      .default(true),
    billingInterval: text("billing_interval")
      .notNull()
      .default(BillingInterval.ONE_TIME)
      .$type<(typeof BillingInterval)[keyof typeof BillingInterval]>(),
    billingIntervalCount: integer("billing_interval_count")
      .notNull()
      .default(1),
    validFrom: integer("valid_from", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),
    validUntil: integer("valid_until", { mode: "timestamp" }),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    createdAt,
    updatedAt,
    deletedAt,
  },
  (t) => [
    index("catalog_prices_item_active_idx").on(t.catalogItemId, t.isActive),
  ],
);

export const promotions = sqliteTable(
  "promotions",
  {
    promotionId: text("promotion_id")
      .primaryKey()
      .notNull()
      .$defaultFn(createId),
    catalogPriceId: text("catalog_price_id")
      .notNull()
      .references(() => catalogPrices.catalogPriceId, { onDelete: "cascade" }),
    name: text("name").notNull(),
    promotionalAmountKurus: integer("promotional_amount_kurus").notNull(),
    startsAt: integer("starts_at", { mode: "timestamp" }).notNull(),
    endsAt: integer("ends_at", { mode: "timestamp" }),
    maxRedemptions: integer("max_redemptions"),
    redeemedCount: integer("redeemed_count").notNull().default(0),
    status: text("status")
      .notNull()
      .default(PromotionStatus.DRAFT)
      .$type<(typeof PromotionStatus)[keyof typeof PromotionStatus]>(),
    createdAt,
    updatedAt,
    deletedAt,
  },
  (t) => [
    index("promotions_price_status_idx").on(t.catalogPriceId, t.status),
    index("promotions_period_idx").on(t.startsAt, t.endsAt),
  ],
);

export const catalogFulfillments = sqliteTable(
  "catalog_fulfillments",
  {
    catalogFulfillmentId: text("catalog_fulfillment_id")
      .primaryKey()
      .notNull()
      .$defaultFn(createId),
    catalogItemId: text("catalog_item_id")
      .notNull()
      .references(() => catalogItems.catalogItemId, { onDelete: "cascade" }),
    fulfillmentType: text("fulfillment_type")
      .notNull()
      .$type<(typeof FulfillmentType)[keyof typeof FulfillmentType]>(),
    referenceId: text("reference_id"),
    privateData: text("private_data", { mode: "json" }).$type<
      Record<string, string>
    >(),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt,
    updatedAt,
    deletedAt,
  },
  (t) => [
    index("catalog_fulfillments_item_idx").on(t.catalogItemId, t.displayOrder),
  ],
);

export const bundleItems = sqliteTable(
  "bundle_items",
  {
    bundleItemId: text("bundle_item_id")
      .primaryKey()
      .notNull()
      .$defaultFn(createId),
    bundleCatalogItemId: text("bundle_catalog_item_id")
      .notNull()
      .references(() => catalogItems.catalogItemId, { onDelete: "cascade" }),
    childCatalogItemId: text("child_catalog_item_id")
      .notNull()
      .references(() => catalogItems.catalogItemId),
    quantity: integer("quantity").notNull().default(1),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt,
    updatedAt,
  },
  (t) => [
    uniqueIndex("bundle_items_bundle_child_unique").on(
      t.bundleCatalogItemId,
      t.childCatalogItemId,
    ),
    index("bundle_items_bundle_order_idx").on(
      t.bundleCatalogItemId,
      t.displayOrder,
    ),
  ],
);

const catalogItemTypeSchema = z.enum([
  CatalogItemType.DIGITAL_PROGRAM,
  CatalogItemType.DIGITAL_BUNDLE,
  CatalogItemType.COACHING,
  CatalogItemType.CONSULTING,
  CatalogItemType.PHYSICAL_PRODUCT,
]);

const catalogItemStatusSchema = z.enum([
  CatalogItemStatus.DRAFT,
  CatalogItemStatus.ACTIVE,
  CatalogItemStatus.ARCHIVED,
]);

const billingIntervalSchema = z.enum([
  BillingInterval.ONE_TIME,
  BillingInterval.MONTH,
]);

const promotionStatusSchema = z.enum([
  PromotionStatus.DRAFT,
  PromotionStatus.ACTIVE,
  PromotionStatus.ENDED,
]);

export const insertCatalogItemSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(3, "Slug en az 3 karakter olmalıdır")
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug yalnızca küçük harf, rakam ve tire içerebilir",
    ),
  itemType: catalogItemTypeSchema,
  name: string.min(2, "Ürün adı en az 2 karakter olmalıdır"),
  description: z.string().trim().default(""),
  imageUrl: z.url().optional(),
  status: catalogItemStatusSchema.optional(),
  isFeatured: z.boolean().optional(),
  displayOrder: z.number().int().min(0).optional(),
});

const catalogPriceSchema = z.object({
  catalogItemId: cuid,
  amountKurus: z.number().int().positive(),
  currency: z.literal("TRY").default("TRY"),
  vatRateBasisPoints: z.literal(2000).default(2000),
  vatIncluded: z.literal(true).default(true),
  billingInterval: billingIntervalSchema.default(BillingInterval.ONE_TIME),
  billingIntervalCount: z.number().int().positive().default(1),
  validFrom: dateTime.optional(),
  validUntil: dateTime.optional(),
  isActive: z.boolean().optional(),
});

const validateCatalogPricePeriod = (
  data: { validFrom?: Date; validUntil?: Date },
  context: z.RefinementCtx,
) => {
  if (data.validFrom && data.validUntil && data.validUntil <= data.validFrom) {
    context.addIssue({
      code: "custom",
      path: ["validUntil"],
      message: "Fiyat geçerlilik bitişi başlangıçtan sonra olmalı",
    });
  }
};

export const insertCatalogPriceSchema = catalogPriceSchema.superRefine(
  validateCatalogPricePeriod,
);

export const insertCatalogPriceWithoutItemSchema = catalogPriceSchema
  .omit({ catalogItemId: true })
  .superRefine(validateCatalogPricePeriod);

export const insertPromotionSchema = z
  .object({
    catalogPriceId: cuid,
    name: string.min(3),
    promotionalAmountKurus: z.number().int().positive(),
    startsAt: dateTime,
    endsAt: dateTime.optional(),
    maxRedemptions: z.number().int().positive().optional(),
    status: promotionStatusSchema.optional(),
  })
  .superRefine((data, context) => {
    if (!data.endsAt && data.maxRedemptions === undefined) {
      context.addIssue({
        code: "custom",
        path: ["endsAt"],
        message: "Kampanya için gerçek bir bitiş tarihi veya kontenjan gerekir",
      });
    }
    if (data.endsAt && data.endsAt <= data.startsAt) {
      context.addIssue({
        code: "custom",
        path: ["endsAt"],
        message: "Kampanya bitişi başlangıçtan sonra olmalı",
      });
    }
  });

export const selectCatalogItemSchema = z.object({
  catalogItemId: cuid,
  slug: z.string(),
  itemType: catalogItemTypeSchema,
  name: z.string(),
  description: z.string(),
  imageUrl: z.string().nullable(),
  status: catalogItemStatusSchema,
  isFeatured: z.boolean(),
  displayOrder: z.number().int(),
  createdAt: dateTime,
  updatedAt: dateTime.nullable(),
  deletedAt: dateTime.nullable(),
});

export type InsertCatalogItem = z.infer<typeof insertCatalogItemSchema>;
export type InsertCatalogPrice = z.infer<typeof insertCatalogPriceSchema>;
export type InsertPromotion = z.infer<typeof insertPromotionSchema>;
export type SelectCatalogItem = z.infer<typeof selectCatalogItemSchema>;
